import { useCallback } from 'react'
import type {
  submitEnrollmentStepRequest,
  useSubmitEnrollmentStep
} from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { mergeSubmittedStepData } from '../../utils/utils'
import type { ApplyHydrationFn } from '../../types'
import { getSelfEnrollmentSubmissionStep } from '../../utils/steps'
import {
  resolveStepSubmitSuccess,
  type StepSubmitSuccessOptions
} from './resolveStepSubmitSuccess'
import type { ValidateStepContinuationOptions } from './validateStepContinuation'

type StepValidationFn = (
  step: number,
  options?: ValidateStepContinuationOptions
) => boolean

type UseStepSubmitActionArgs = {
  applyHydration: ApplyHydrationFn
  enrollmentSessionToken: string | null
  goToStep: (step: number) => void
  scrollToTop: () => void
  setIsCheckingStepAccess: (value: boolean) => void
  setStepSubmissionError: (value: string | null) => void
  submitEnrollmentStepMutation: ReturnType<typeof useSubmitEnrollmentStep>
  suppressNextSessionStepSync: () => void
  validateStepBeforeContinue: StepValidationFn
}

export function useStepSubmitAction({
  applyHydration,
  enrollmentSessionToken,
  goToStep,
  scrollToTop,
  setIsCheckingStepAccess,
  setStepSubmissionError,
  submitEnrollmentStepMutation,
  suppressNextSessionStepSync,
  validateStepBeforeContinue
}: UseStepSubmitActionArgs) {
  const setResumedEnrollment = useEnrollmentStore(state => state.setResumedEnrollment)

  return useCallback(
    async (
      step: number,
      data: Parameters<typeof submitEnrollmentStepRequest>[0]['data'],
      options?: StepSubmitSuccessOptions
    ) => {
      const canContinue = validateStepBeforeContinue(step, {
        setInlineError: true,
        keepLoadingOnSuccess: true
      })

      scrollToTop()

      if (!canContinue || !enrollmentSessionToken) {
        setIsCheckingStepAccess(false)
        return
      }

      setStepSubmissionError(null)

      try {
        const response = await submitEnrollmentStepMutation.mutateAsync({
          sessionToken: enrollmentSessionToken,
          data: {
            ...data,
            step: getSelfEnrollmentSubmissionStep(step)
          }
        })

        const mergedEnrollment = mergeSubmittedStepData(response, data)
        setResumedEnrollment(mergedEnrollment)
        applyHydration({ enrollment: mergedEnrollment, syncStep: false })
        resolveStepSubmitSuccess({
          response,
          step,
          options,
          suppressNextSessionStepSync,
          goToStep
        })
      } finally {
        setIsCheckingStepAccess(false)
      }
    },
    [
      applyHydration,
      enrollmentSessionToken,
      goToStep,
      scrollToTop,
      setResumedEnrollment,
      setIsCheckingStepAccess,
      setStepSubmissionError,
      submitEnrollmentStepMutation,
      suppressNextSessionStepSync,
      validateStepBeforeContinue
    ]
  )
}
